import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { Market } from "../api/types";
import { useNow } from "../hooks/useNow";
import { formatWindow, parseSeriesSlug, windowPhase, type SeriesParts } from "../lib/series";
import { Empty, Panel } from "./Panel";

interface Props {
  markets: Market[];
  error: Error | null;
  loading: boolean;
}

/** Строка таблицы: рынок вместе с разобранным slug'ом. */
interface Row {
  market: Market;
  parts: SeriesParts | null;
}

const ANY = "все";

export function MarketsPanel({ markets, error, loading }: Props) {
  const [query, setQuery] = useState("");
  const [onlyTracked, setOnlyTracked] = useState(false);
  const [asset, setAsset] = useState(ANY);
  const [duration, setDuration] = useState(ANY);

  // Раз в секунду: от этого зависит только пометка идущего окна, а у
  // пятиминутного рынка она обязана гаснуть вовремя. Сам список строк от `now`
  // НЕ зависит (см. useMemo ниже) — тик пересчитывает лишь фазу и подпись окна.
  const nowMs = useNow(1000);
  const now = useMemo(() => new Date(nowMs), [nowMs]);

  const parsed = useMemo<Row[]>(
    () => markets.map((m) => ({ market: m, parts: parseSeriesSlug(m.slug) })),
    [markets],
  );

  // Наборы для фильтров считаются по ФАКТИЧЕСКИМ рынкам, а не задаются списком:
  // появится третий актив — фильтр узнает о нём сам.
  const assets = useMemo(
    () => [...new Set(parsed.map((r) => r.parts?.asset).filter(Boolean) as string[])].sort(),
    [parsed],
  );
  const durations = useMemo(() => {
    const seen = new Map<string, number>();
    for (const r of parsed) if (r.parts) seen.set(r.parts.duration, r.parts.durationSec);
    return [...seen.entries()].sort((a, b) => a[1] - b[1]).map(([d]) => d);
  }, [parsed]);

  // Фильтрация на клиенте: эндпоинт и так отдаёт не больше 500 строк, уже
  // упорядоченных «отслеживаемые сверху». Серверный поиск был бы лишним кругом.
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = parsed.filter(({ market: m, parts }) => {
      if (onlyTracked && !m.tracked) return false;
      // Рынок вне серии не имеет ни актива, ни длительности, поэтому любой из
      // этих фильтров его прячет — это и есть смысл фильтра.
      if (asset !== ANY && parts?.asset !== asset) return false;
      if (duration !== ANY && parts?.duration !== duration) return false;
      if (!q) return true;
      return (
        m.question.toLowerCase().includes(q) ||
        m.slug.toLowerCase().includes(q) ||
        m.condition_id.toLowerCase().includes(q)
      );
    });

    // Порядок: сначала в работе, потом актив, потом длительность, потом время
    // окна. Без сортировки по времени соседние окна одной серии расходятся по
    // таблице, и глазами их не собрать.
    return out.sort((a, b) => {
      if (a.market.tracked !== b.market.tracked) return a.market.tracked ? -1 : 1;
      const ap = a.parts;
      const bp = b.parts;
      if (ap && bp) {
        if (ap.asset !== bp.asset) return ap.asset < bp.asset ? -1 : 1;
        if (ap.durationSec !== bp.durationSec) return ap.durationSec - bp.durationSec;
        return ap.start.getTime() - bp.start.getTime();
      }
      // Рынки вне серии — в конец: они долгоиграющие, и место им не в начале
      // списка, который перетряхивается каждые пять минут.
      if (ap || bp) return ap ? -1 : 1;
      return a.market.question.localeCompare(b.market.question);
    });
  }, [parsed, query, onlyTracked, asset, duration]);

  return (
    <Panel
      title="Рынки"
      subtitle={markets.length ? `${rows.length} из ${markets.length}` : undefined}
    >
      {loading && <Empty>загрузка…</Empty>}
      {error && <p className="text-sm text-[var(--color-sell)]">{error.message}</p>}

      {!loading && !error && markets.length === 0 && (
        <Empty>
          Рынков пока нет. Их выгружает трейдер по <code className="num">MD_TRACK_SERIES</code> и{" "}
          <code className="num">MD_TRACK_MARKETS</code>.
        </Empty>
      )}

      {markets.length > 0 && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="поиск по вопросу или slug"
              className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)]"
            />
            {assets.length > 1 && (
              <Chips label="актив" value={asset} options={assets} onChange={setAsset} />
            )}
            {durations.length > 1 && (
              <Chips label="окно" value={duration} options={durations} onChange={setDuration} />
            )}
            {/* cursor-pointer на подписи: клик по ней переключает чекбокс так же,
                как клик по квадратику, и курсор обязан говорить об этом одинаково.
                Утилитой, а не правилом в index.css: такая подпись в приложении
                одна, а общее правило пришлось бы писать через :has(). */}
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-[var(--color-muted)]">
              <input
                type="checkbox"
                checked={onlyTracked}
                onChange={(e) => setOnlyTracked(e.target.checked)}
              />
              только в работе
            </label>
          </div>

          {rows.length === 0 ? (
            <Empty>Ничего не найдено.</Empty>
          ) : (
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[var(--color-panel)]">
                  <tr className="text-left text-xs text-[var(--color-muted)]">
                    <th className="pb-2 font-normal">Актив</th>
                    <th className="pb-2 font-normal">Окно</th>
                    <th className="pb-2 font-normal">Период</th>
                    <th className="pb-2 font-normal">Исходов</th>
                    <th className="pb-2 font-normal">Шаг</th>
                    <th className="pb-2 font-normal">Мин. размер</th>
                    <th className="pb-2 font-normal">В работе</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ market: m, parts }) => {
                    const phase = parts ? windowPhase(parts, now) : null;
                    return (
                      <tr key={m.condition_id} className="border-t border-[var(--color-border)]">
                        <td className="py-2 pr-4">
                          {/* Настоящая ссылка, а не onClick: средняя кнопка мыши,
                              «открыть в новой вкладке» и «копировать адрес» должны
                              работать так же, как везде. */}
                          <Link
                            to={`/market/${encodeURIComponent(m.condition_id)}`}
                            title={m.question || m.slug || m.condition_id}
                            className="hover:text-[var(--color-accent)] hover:underline"
                          >
                            {parts ? parts.asset : m.question || m.slug || m.condition_id}
                          </Link>
                        </td>
                        <td className="num py-2 pr-4 text-[var(--color-muted)]">
                          {parts ? parts.duration : "—"}
                        </td>
                        <td className="num py-2 pr-4">
                          {parts ? (
                            <span
                              // Прошедшее окно приглушается, а не прячется: рынок
                              // ещё может быть открыт, и оператор вправе видеть его.
                              className={phase === "past" ? "text-[var(--color-muted)]" : undefined}
                            >
                              {formatWindow(parts, now)}
                              {phase === "live" && (
                                <span className="ml-2 text-xs" style={{ color: "var(--color-buy)" }}>
                                  сейчас
                                </span>
                              )}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="num py-2 pr-4 text-[var(--color-muted)]">
                          {m.tokens.length}
                        </td>
                        <td className="num py-2 pr-4">{m.tick_size}</td>
                        <td className="num py-2 pr-4">{m.min_order_size}</td>
                        <td className="py-2">
                          {m.tracked ? (
                            <span style={{ color: "var(--color-buy)" }}>да</span>
                          ) : (
                            <span className="text-[var(--color-muted)]">нет</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

/** Переключатель-«таблетки». Список значений короткий и известен заранее,
 *  поэтому кнопки, а не выпадающий список: один клик вместо двух. */
function Chips({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className="text-[var(--color-muted)]">{label}</span>
      {[ANY, ...options].map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`num rounded border px-2 py-1 ${
            value === o
              ? "border-[var(--color-accent)] text-[var(--color-accent)]"
              : "border-[var(--color-border)] text-[var(--color-muted)] hover:border-[var(--color-accent)]"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}
